/**
 * Whether a body from a Jot Server may be written over what the panel holds.
 *
 * The one decision in Jot that can destroy the user's text, so it is a pure
 * function of facts handed in, and it lives on its own. A pull is a race between a
 * network round trip and a 400 ms debounce; a race is exactly what a call site
 * cannot be read for, which is why the rule is here and covered by
 * `tests/pull-guard.test.ts` rather than argued for in a comment beside the write.
 *
 * Two facts have to hold (issue #6), and the second is the one that looks
 * redundant. The editor is live from the first paint and saves on a debounce, so
 * the *persisted* body can equal the Last-Agreed Body while the editor holds a
 * sentence that is not persisted yet. A pull authorised on the persisted body alone
 * overwrites that sentence — the same race `platform.onNotesChanged` is deliberately
 * left unsubscribed to avoid (`app-store.tsx`). So the sync layer has to ask the
 * panel, and this is the one place the two are coupled.
 *
 * What the editor is compared against is the body *the pass read before its round
 * trip*, never a fresh read, and that is what makes this a compare-and-swap rather
 * than a check. The engine decided to pull because that body was the one the two
 * sides last agreed on; anything else in the editor now — typed since, or saved
 * while the request was out — means the local side has moved since the agreement,
 * which is half of the Conflict predicate and never something to write over.
 *
 * Which presumes there *is* a body to compare against, and the case where there is
 * not is the one this guard got wrong. A Scratchpad that storage no longer holds is
 * the restore ADR-0002 exists for, and it arrived here as `comparedBody: ''` — so a
 * panel still showing the text whose file had been removed read as "the editor is
 * ahead", and the restore came out as a Pending Conflict nothing can clear (#7), over
 * two bodies that could be byte identical. Absence is therefore its own variant
 * (`ComparedScratchpad`) and answers a different question, below.
 *
 * Every refusal is an answer rather than a failure, and which one it is decides what
 * the engine records: see `PullVerdict`.
 */

import type { NoteId } from '@shared/types'

/** What the panel holds, read in one synchronous step with the write it authorises. */
export interface PanelText {
  /** The Note the panel has open, or null while it has none. */
  openId: NoteId | null
  /** The editor's live text: the bytes a pull would overwrite. */
  liveBody: string
  /**
   * Whether any write is queued, at any id.
   *
   * The issue's first condition, and it is about the save path rather than the
   * editor: a queued body is text that is not on disk yet, so a pull applied across
   * it either loses that text or is itself lost, depending on which write lands
   * last. Any id and not just this one, because a queue that still names the Note
   * the panel has moved off is a save path mid-move (`adoptScratchpad`), which is
   * not a state worth reasoning about when waiting one pass costs nothing.
   */
  queuedWrites: boolean
  /**
   * The last body this panel applied from a pull, or null.
   *
   * It exists for one ordering, and without it that ordering wedges the device. A
   * pull whose write does not land leaves the editor holding the Jot Server's body
   * while storage still holds the older one, and the next pass would read that
   * difference as the user having typed — recording a Pending Conflict that nothing
   * in the project can clear yet (#7), over a body the user never touched. Those
   * bytes came from the server, so they are not a local change and cannot be half
   * of a Conflict; the pull is merely still on its way to disk, which is `queued`.
   *
   * Cleared by the first keystroke, so it can only ever excuse text the user has
   * not touched.
   */
  appliedPull: string | null
}

/**
 * What the pass found at `PullTarget.comparedId` before its round trip, in the two
 * answers that authorise a pull for different reasons.
 *
 * A union rather than a string, because the two are not comparable facts and were
 * conflated once: `readNote` returns null only for genuine absence and throws
 * otherwise (issue #12), so "there is no Note" is something the engine knows exactly,
 * and flattening it to `''` handed the guard a body the panel had never held. What
 * makes the distinction load-bearing is that they lead to opposite verdicts over the
 * same editor text — a body means "the editor must still match it", while absence
 * means "there is no local copy left to lose", which is a restore.
 *
 * `unreadable` is deliberately not a third member. A Scratchpad that is there and out
 * of reach never reaches a pull at all: `runOnce` ends the pass before a request is
 * made, because `atomicWrite` publishes by renaming over the target, so a pull
 * authorised over an unreadable file would take the local copy with it.
 */
export type ComparedScratchpad =
  /** Storage held this body at `comparedId`, and the editor has to still match it. */
  | { kind: 'body'; body: string }
  /**
   * Storage held no Note at `comparedId` — genuinely none.
   *
   * Absence is never an intent to delete (ADR-0002), so it is not a local change and
   * cannot be half of a Conflict; what is left to protect is the editor, which may be
   * holding the only copy of something now that storage holds none.
   *
   * `agreedBody` is what the two sides last agreed on, or null when there is no
   * agreement on record (Pairing). It is the evidence that an editor differing from
   * the Jot Server's body is nevertheless not a local change: a panel whose file was
   * removed under it still shows the body it opened, and that body is the one the two
   * agreed on. Never used as a body to write, and never as a body to push — it is
   * only ever what the editor is recognised by.
   */
  | { kind: 'absent'; agreedBody: string | null }

/** The body a pass proposes to write, and the local read that authorised it. */
export interface PullTarget {
  /**
   * Where the pulled body belongs: the Jot Server's Scratchpad id. Not necessarily
   * the one the panel has open — a device pairing to a server that is ahead of it
   * adopts the id (ADR-0001) and takes the body in the same step.
   */
  id: NoteId
  /** The Jot Server's body: the only bytes from a server Jot ever writes locally. */
  body: string
  /**
   * The id `compared` was read from, which the panel must still be on. Equal to
   * `id` except while pairing, and checked rather than assumed because comparing the
   * editor against some other Note's body would authorise a write on no evidence.
   */
  comparedId: NoteId
  /**
   * The Scratchpad as the pass read it out of storage, *before* the round trip.
   *
   * Deliberately stale, and that is the point: it is the value the decision to pull
   * was made against, so the editor differing from it is the whole of "the local side
   * moved since". A fresh read would answer a different question — whether the editor
   * agrees with disk *now* — and would quietly bless a save that landed while the
   * request was in flight, which is a local change like any other.
   */
  compared: ComparedScratchpad
}

export type PullVerdict =
  /** Both conditions hold: nothing the panel holds is at risk, so write it. */
  | 'apply'
  /**
   * The editor holds text that is neither what storage holds nor what a pull put
   * there, so the local body has changed since the two last agreed — and the
   * server's has too, or there would have been nothing to pull. That is the
   * Conflict, established from the bodies exactly as the engine's other two sites
   * establish it and never inferred from a status code.
   */
  | 'ahead'
  /**
   * A write is still queued, and nothing about either side is proven: the queued
   * body may be the same text storage already holds. Nothing is recorded, nothing is
   * written, and the next pass asks again — Waiting rather than a Conflict, which is
   * what keeps a device out of a Pending Conflict it did not earn.
   */
  | 'queued'
  /**
   * The panel has no Scratchpad open, or has moved off the Note that was read. There
   * is nothing to compare the pulled body against and nobody holding the text it
   * would replace.
   */
  | 'unopened'
  /**
   * Storage holds no Note, nothing is queued to put one there, and the editor holds
   * text that is neither the Jot Server's body nor the one the two sides agreed on —
   * so the panel is holding the only copy of it, and a pull would be what destroyed
   * it.
   *
   * Not `ahead`, and the difference is the point. `ahead` is half of a Conflict, and
   * a Conflict recorded here would be a claim about a local body that no Note holds:
   * the Resolution it needs (#7) offers the *stored* local body, which is the one
   * thing this state has not got. Nothing is recorded instead, which is also all that
   * has to happen — the first keystroke recreates the Note through the ordinary save
   * path, and the next pass is an ordinary push, or the `409` that establishes the
   * Conflict from bodies rather than from an absent read.
   */
  | 'unpersisted'

/**
 * The guard.
 *
 * `ahead` is tested before `queued` because typing sets both — a keystroke queues a
 * write *and* puts the editor ahead of disk — and of the two answers only `ahead`
 * says what happened. Which matters: it is the answer that raises the Conflict the
 * acceptance criteria ask for, where `queued` would silently drop the server's
 * change on the floor and try again forever while the user keeps typing.
 *
 * The absent branch reverses that order, because nothing there raises a Conflict and
 * so nothing is lost by naming the queue first: `unpersisted` then means exactly
 * "storage holds no Note, nothing is on its way to make one, and the editor is the
 * only copy", which is a state worth reporting rather than a state that has to be
 * disentangled from a save in flight.
 */
export function guardPull(panel: PanelText, target: PullTarget): PullVerdict {
  if (panel.openId === null || panel.openId !== target.comparedId) return 'unopened'

  if (target.compared.kind === 'absent') {
    if (panel.queuedWrites) return 'queued'

    /*
     * Whether the editor is holding text that exists somewhere other than the editor,
     * which is the whole of what a pull may write over once storage holds no copy.
     * Four ways to be true and they are one rule, not four cases:
     *
     * - Nothing at all. A recorded Scratchpad with no Note at it opens as an empty
     *   placeholder (`app-store.tsx`), which is the shape a wiped notes folder arrives
     *   in and the restore that has to work.
     * - The Jot Server's own body. The write is then a restore of bytes both sides
     *   hold, whatever else is true of them.
     * - What a pull last put there, which is not a local change for the reason
     *   `appliedPull` gives.
     * - The Last-Agreed Body, and this is the case a flattened `''` got wrong: a panel
     *   that settled and then had its file removed under it — `IPC.openNotesFolder`
     *   opens that folder while the panel runs, and other tools writing these files is
     *   the point of the storage format (ADR-0004) — still shows the body the two sides
     *   agreed on. The local side has not changed; only the local copy has gone.
     */
    const heldElsewhere =
      panel.liveBody === '' ||
      panel.liveBody === target.body ||
      panel.liveBody === panel.appliedPull ||
      panel.liveBody === target.compared.agreedBody

    return heldElsewhere ? 'apply' : 'unpersisted'
  }

  if (panel.liveBody !== target.compared.body && panel.liveBody !== panel.appliedPull) {
    return 'ahead'
  }
  if (panel.queuedWrites) return 'queued'
  return 'apply'
}
